/**
 * M7 S37 part 2: the story props view. Draws `storyScene`'s entries (the Column Nine hulks and memorial, the barge
 * wreck or rig) through the asset contract, like the structure views: `models.get(assetId)` → `instantiate` →
 * `applyStaticLook(look)` → `enableShadows`, pooled per (asset id, look). Models that are not loaded yet are loaded
 * through the Reach model cache's `ensure` when it has one, and drawn once they are.
 *
 * - **Rebuild** (a rare event): when the system, the Career's story traces object or (for a stripped field) the
 *   online day changes; `watch` compares them by reference and value, allocation-free.
 * - **Per frame** (allocation-free, D-014): each root into render space (float64 world → camera-relative, D-013).
 * - **Salvage entries are not drawn here** (S37d): the barge wreck is a salvage-world wreck (`story-link/bargeWreck.ts`),
 *   drawn by the wreck views, so drawing its scene entry too would double it.
 */
import { Group, type Object3D } from 'three/webgpu'
import type { GameContext } from '../../app/feature'
import { logger } from '../../core/logger'
import type { Vec3 } from '../../core/math'
import type { ModelSource } from '../../reach/runtime/types'
import { enableShadows } from '../../render/environment'
import { toRenderSpace } from '../../render/floatingOrigin'
import { applyStaticLook } from '../../render/materials'
import type { StoryTraces } from '../../story/types'
import type { StoryProp } from '../../story-link/missions/scene'
import { createViewPool, poolKey } from '../structures/plan'

/** What a rebuild depends on; compared every frame without allocating. */
export interface StoryPropsWatch {
  systemId: string | null
  traces: StoryTraces | undefined | null
  onlineDays: number
}

export function createPropsWatch(): StoryPropsWatch {
  return { systemId: null, traces: null, onlineDays: -1 }
}

/** True (and the watch updated) when the entries may have changed. Allocation-free. */
export function watchChanged(w: StoryPropsWatch, systemId: string | null, traces: StoryTraces | undefined, onlineDays: number): boolean {
  if (w.systemId === systemId && w.traces === traces && w.onlineDays === onlineDays) return false
  w.systemId = systemId
  w.traces = traces
  w.onlineDays = onlineDays
  return true
}

interface PropView {
  readonly prop: StoryProp
  readonly key: string
  readonly root: Object3D
}

type Ensuring = ModelSource & { ensure?: (ids: readonly string[]) => Promise<void> }

export interface StoryPropsView {
  readonly group: Group
  readonly count: number
  /** Replaces the drawn entries (a rare event). */
  set(ctx: GameContext, props: readonly StoryProp[]): void
  /** Per frame, allocation-free. */
  frame(cam: Readonly<Vec3>): void
  dispose(): void
}

export function createStoryPropsView(scene: Object3D, poolPerKey = 4): StoryPropsView {
  const group = new Group()
  group.name = 'storyProps'
  scene.add(group)
  const pool = createViewPool<Object3D>(poolPerKey)
  let views: PropView[] = []
  let generation = 0
  const warned = new Set<string>()

  function build(ctx: GameContext, models: ModelSource, p: StoryProp): Object3D | null {
    const key = poolKey(p.assetId, p.look)
    const pooled = pool.acquire(key)
    if (pooled !== undefined) return pooled
    const model = models.get(p.assetId)
    if (model === undefined) return null
    const instance = model.instantiate(ctx.views.lodScale)
    applyStaticLook(ctx.lib, instance, p.look)
    enableShadows(instance.root)
    instance.root.name = `story:${p.key}`
    return instance.root
  }

  function place(ctx: GameContext, models: ModelSource, props: readonly StoryProp[]): void {
    for (const v of views) {
      v.root.removeFromParent()
      pool.release(v.key, v.root)
    }
    views = []
    for (const p of props) {
      const root = build(ctx, models, p)
      if (root === null) continue
      const q = p.rotation
      root.quaternion.set(q[0], q[1], q[2], q[3])
      group.add(root)
      views.push({ prop: p, key: poolKey(p.assetId, p.look), root })
    }
  }

  return {
    group,
    get count(): number {
      return views.length
    },
    set(ctx, all): void {
      const reach = ctx.reach
      if (reach === null) return
      const props = all.filter((p) => !p.salvage)
      const models = reach.models as Ensuring
      const gen = ++generation
      place(ctx, models, props)
      const missing = props.filter((p) => models.get(p.assetId) === undefined).map((p) => p.assetId)
      if (missing.length === 0 || models.ensure === undefined) return
      models.ensure(missing).then(
        () => {
          if (gen === generation) place(ctx, models, props)
        },
        (err: unknown) => {
          const id = missing.join(',')
          if (warned.has(id)) return
          warned.add(id)
          logger.warn(`storyProps: ${id} did not load; those story props are not drawn`, err)
        },
      )
    },
    frame(cam): void {
      for (let i = 0; i < views.length; i++) {
        const p = views[i].prop.position
        toRenderSpace(p[0], p[1], p[2], cam, views[i].root.position)
      }
    },
    dispose(): void {
      generation++
      for (const v of views) v.root.removeFromParent()
      views = []
      pool.clear(() => undefined)
      group.removeFromParent()
    },
  }
}
